<template>
  <Stack space="medium">
    <OrderFormBody
      ref="orderFormBody"
      :active-step="1"
      :description="
        order_form?.form_settings.steps?.step1?.description ??
        'Please enter your address below. If the address is at an apartment or unit, please make sure to note which one.'
      "
      :isNextLoading="addressLoading || submittingForm"
      :primaryColor="appearance.primary_color"
      :steps="order_form?.form_settings?.steps"
      :title="order_form?.form_settings.steps?.step1?.title ?? 'Address'"
      :orderFormId="order_form?.id"
      @onNextClick="handleNextClick"
    >
      <Stack space="large">
        <AddressSelector
          :address="addressForm"
          :addressErrors="addressErrors"
          @onAddressChange="handleAddressUpdate"
          @onAddressSelection="handleAddressSelection"
        />
        <template
          v-if="custom_fields?.data.length || order_form_product_filter_applications?.data.length"
        >
          <Box marginX="-gutter">
            <Divider />
          </Box>
          <Form>
            <template v-if="order_form_product_filter_applications?.data">
              <template v-for="filter_application in order_form_product_filter_applications?.data">
                <FormCurrencyInput
                  v-if="filter_application.product_filter.type === 'price'"
                  :key="filter_application.id"
                  :error="
                    formattedErrors[
                      `product_filter_entries.${filter_application.product_filter.id}.value`
                    ]
                  "
                  :value="productFilterFieldData[filter_application.product_filter.id]?.value"
                  :label="filter_application.product_filter.name"
                  :helperText="filter_application.product_filter.description"
                  :name="filter_application.product_filter.id"
                  @update:modelValue="handleProductFilterFieldUpdate"
                />
                <FormTextInput
                  v-else
                  :key="filter_application.product_filter.id"
                  :error="
                    formattedErrors[
                      `product_filter_entries.${filter_application.product_filter.id}.value`
                    ]
                  "
                  :value="productFilterFieldData[filter_application.product_filter.id]?.value"
                  :label="filter_application.product_filter.name"
                  :helperText="filter_application.product_filter.description"
                  :name="filter_application.product_filter.id"
                  type="number"
                  @update:modelValue="handleProductFilterFieldUpdate"
                />
              </template>
            </template>
            <template v-if="custom_fields?.data">
              <component
                v-for="customField in custom_fields.data"
                :key="customField.id"
                :is="
                  customField.type === 'text' || customField.type === 'select'
                    ? FormInputWrapper
                    : 'div'
                "
              >
                <CustomFieldInput
                  :customField="customField"
                  :value="customFieldData[customField.id]?.value ?? null"
                  :error="formattedErrors[`custom_fields.${customField.id}.value`]"
                  @change="handleCustomFieldUpdate"
                />
              </component>
            </template>
          </Form>
        </template>
      </Stack>
    </OrderFormBody>
    <SimpleAlert
      v-if="is_editing"
      type="info"
      description="Please use the step navigator to your left to switch between steps in editing mode."
    />
  </Stack>
</template>

<script setup>
import { defineOptions, onMounted, ref } from 'vue'
import { Box, Divider, Stack } from '@aryeo/runway'
import { router, useForm } from '@inertiajs/vue3'
import keyBy from 'lodash/keyBy'

import Layout from '@/Layouts/OrderForm/OrderFormLayout.vue'

import DraftOrderApi from '@/Services/Api/Company/V1/DraftOrderApi'

import { useAddressSelector } from '@/Hooks/Company/AddressSelector/useAddressSelector'
import { useApiErrors } from '@/Hooks/Shared/useApiErrors'

import Form from '@/Runway/Forms/FormLayout/Form.vue'
import FormInputWrapper from '@/Runway/Forms/FormInputs/FormInputWrapper.vue'
import FormTextInput from '@/Runway/Forms/FormInputs/FormTextInput.vue'
import FormCurrencyInput from '@/Runway/Forms/FormInputs/FormCurrencyInput.vue'
import SimpleAlert from '@/Runway/Alerts/Simple.vue'

import CustomFieldInput from '@/Components/Shared/Orders/CustomFields/CustomFieldInput.vue'
import AddressSelector from '@/Components/Shared/AddressSelector/AddressSelector.vue'
import OrderFormBody from '@/Components/Customer/OrderForms/LayoutParts/OrderFormBody.vue'

defineOptions({
  layout: Layout,
})

const props = defineProps({
  appearance: {
    type: Object,
    default: () => ({}),
  },
  is_editing: {
    type: Boolean,
    default: false,
  },
  order_form: {
    type: Object,
    default: () => ({}),
  },
  order_form_session: {
    type: Object,
    default: () => ({}),
  },
  order: {
    type: Object,
    default: () => ({}),
  },
  active_step: {
    type: Number,
  },
  custom_fields: {
    type: Object,
    default: () => ({}),
  },
  filter_by_square_feet_enabled: {
    type: Boolean,
    default: false,
  },
  filter_by_price_enabled: {
    type: Boolean,
    default: false,
  },
  order_form_product_filter_applications: {
    type: Object,
    default: () => ({}),
  },
  order_form_product_filter_entries: {
    type: Array,
    default: () => [],
  },
})

const { formattedErrors, setErrors } = useApiErrors()
const {
  addressErrors,
  addressForm,
  handleAddressSelection,
  handleAddressStore,
  handleAddressUpdate,
  addressLoading,
} = useAddressSelector(props.order.address)

const orderFormBody = ref(null)
const customFieldData = ref({})
const productFilterFieldData = ref({})
const submittingForm = ref(false)

onMounted(() => {
  if (window.self === window.top) {
    requestAnimationFrame(() => {
      orderFormBody.value.$el.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'smooth',
      })
    })
  }

  const customFields = props.custom_fields?.data.map((field) => ({
    id: field.id,
    title: field.title,
    value: field.value ?? '',
    type: field.type,
    step: field.step,
  }))
  customFieldData.value = keyBy(customFields, 'id')
  form.custom_fields = customFieldData.value

  const productFilterApplications =
    props.order_form_product_filter_applications?.data?.map((filter) => {
      return {
        product_filter_id: filter.product_filter.id,
        title: filter.product_filter.name,
        type: filter.product_filter.type,
        value:
          props.order_form_product_filter_entries.length > 0
            ? props.order_form_product_filter_entries.find(
                (e) => e.product_filter_id === filter.product_filter.id
              )?.value
            : null,
      }
    }) ?? []

  productFilterFieldData.value = keyBy(productFilterApplications, 'product_filter_id')
  form.product_filter_entries = productFilterFieldData.value
})

const form = useForm({
  address_id: props.order.address?.id ?? '',
  filter_by_square_feet: props.order.filter_by_square_feet ?? undefined,
  filter_by_price: props.order.filter_by_list_price ?? undefined,
  custom_fields: [],
  product_filter_entries: [],
})

const handleCustomFieldUpdate = (id, value) => {
  const fieldData = customFieldData.value[id] ?? null
  if (!fieldData) {
    return
  }

  customFieldData.value = {
    ...customFieldData.value,
    [id]: {
      ...fieldData,
      value,
    },
  }

  form.custom_fields = customFieldData.value
}

function handleProductFilterFieldUpdate({ key, value }) {
  const fieldData = productFilterFieldData.value[key] ?? null

  if (!fieldData) {
    return
  }

  productFilterFieldData.value = {
    ...productFilterFieldData.value,
    [key]: {
      ...fieldData,
      value,
    },
  }

  form.product_filter_entries = productFilterFieldData.value
}

const handleNextClick = async () => {
  if (props.is_editing) return

  if (addressForm.id && addressForm.isDirty) {
    await handleAddressStore()
  }

  if (!addressForm.id) {
    await handleAddressStore()
  }

  submittingForm.value = true

  DraftOrderApi.storeAddressStep(props.order.id, {
    ...form.data(),
    address_id: addressForm.id,
  })
    .then((res) => {
      router.visit(
        route('web.customer.order-forms.show', {
          order_id: res.data.data.id,
          order_form: props.order_form.id,
        })
      )
    })
    .catch((err) => {
      setErrors(err.response?.data?.data)
      submittingForm.value = false
    })
}
</script>
